import { useEffect } from 'react'
import { crumbsFor } from '@utils/route'
import { useTranslation } from '@/i18n'

const APP_NAME = 'OoKey'
const SITE_ORIGIN = 'https://ookey.jp'

/** og:locale 用の言語コード対応表。 */
const OG_LOCALE: Record<string, string> = { ja: 'ja_JP', en: 'en_US' }

/**
 * 公開 (検索インデックス対象) のマーケティング route 一覧。
 * これら以外 (#home / #vault / #settings / 等のログイン後画面) は per-route description
 * を当てず、汎用 description + `noindex` を返す方針 (将来的に必要なら拡張する)。
 */
const PUBLIC_ROUTES = new Set([
  '',
  'landing',
  'home',
  'docs',
  'contact',
  'download',
  'login',
  'register',
  'forgot',
  'forgot/recovery-key',
  'forgot/hint',
  'forgot/emergency',
  'terms',
  'privacy',
])

/**
 * route → per-route SEO description の i18n key を返す。未定義の route は
 * `common.seo.description` (汎用) を使う。
 */
const seoKeyFor = (route: string): string => {
  const r = route.replace(/^#/, '').split('?')[0]?.split('/')[0] ?? ''

  switch (r) {
    case '':
    case 'landing':
      return 'common.seo.desc_landing'
    case 'docs':
      return 'common.seo.desc_docs'
    case 'contact':
      return 'common.seo.desc_contact'
    case 'download':
      return 'common.seo.desc_download'
    case 'login':
      return 'common.seo.desc_login'
    case 'register':
      return 'common.seo.desc_register'
    case 'forgot':
      return 'common.seo.desc_forgot'
    case 'terms':
      return 'common.seo.desc_terms'
    case 'privacy':
      return 'common.seo.desc_privacy'
    default:
      return 'common.seo.description'
  }
}

/** name / property セレクタで `<meta>` を引き、content を更新する (無ければ作成)。 */
const setMeta = (selector: string, content: string): void => {
  const el = document.head.querySelector<HTMLMetaElement>(selector)

  if (el) el.content = content
}

/**
 * <link rel="canonical"> / <link rel="alternate" hreflang="…"> を更新する。
 * 要素が存在しなければ作成、存在すれば href を上書きする。
 */
const setLink = (rel: string, hreflang: string | null, href: string): void => {
  const selector =
    hreflang === null ? `link[rel="${rel}"]` : `link[rel="${rel}"][hreflang="${hreflang}"]`

  let el = document.head.querySelector<HTMLLinkElement>(selector)

  if (!el) {
    el = document.createElement('link')
    el.rel = rel
    if (hreflang !== null) el.hreflang = hreflang
    document.head.appendChild(el)
  }
  el.href = href
}

/**
 * route / 言語に応じて document の `<head>` を更新する。
 *
 *   - `document.title` — route 別タブ名「<ページ名> — OoKey」(home / landing はアプリ名のみ)。
 *     ページ名は `crumbsFor(route)` のパンくず i18n key を翻訳して導出。
 *   - `<html lang>` — 現在言語。
 *   - description / Open Graph / Twitter Card メタ — route 別の i18n key
 *     (`common.seo.desc_<route>`) を引き、未定義 route は `common.seo.description` 汎用。
 *   - `<link rel="canonical">` — 現在 route の絶対 URL。
 *   - `<link rel="alternate" hreflang="…">` — ja / en / x-default の 3 リンクを route ごとに更新。
 *   - robots — 非公開 route (#vault / #settings 等) は `noindex, nofollow` に切替えて
 *     検索インデックスから除外する。
 *
 * index.html の静的メタは ja 既定 (no-JS / crawler 用) で、本 hook が実行時に
 * ユーザーの言語へ localize する。言語切替時も `t` の identity 変化で再実行される。
 */
export const useDocumentHead = (route: string): void => {
  const { t, language } = useTranslation()

  useEffect(() => {
    document.documentElement.lang = language

    // ── タブ名 (route 別) ──
    const crumbs = crumbsFor(route)
    let label = ''

    for (let i = crumbs.length - 1; i >= 0; i -= 1) {
      const seg = crumbs[i] ?? ''
      const translated = t(seg)

      if (translated !== '' && translated !== seg) {
        label = translated
        break
      }
    }

    document.title = label !== '' && route !== 'home' ? `${label} — ${APP_NAME}` : APP_NAME

    // ── description / OG / Twitter メタ (route + 言語別) ──
    const descKey = seoKeyFor(route)
    let description = t(descKey)

    // 未訳 fallback: route 専用 key が未定義なら汎用 description を当てる
    if (description === descKey) description = t('common.seo.description')

    const ogTitle = t('common.seo.og_title')
    const ogDescription = description

    setMeta('meta[name="description"]', description)
    setMeta('meta[property="og:title"]', ogTitle)
    setMeta('meta[property="og:description"]', ogDescription)
    setMeta('meta[property="og:locale"]', OG_LOCALE[language] ?? 'ja_JP')
    setMeta('meta[name="twitter:title"]', ogTitle)
    setMeta('meta[name="twitter:description"]', ogDescription)

    // ── canonical URL ──
    // SPA + hash router のため、route そのものを fragment として保持する。
    // 「landing」や空 route はルート (`/`) に正規化する。
    const r = (route ?? '').replace(/^#/, '')
    const canonicalPath = r === '' || r === 'landing' ? '/' : `/#${r}`
    const canonicalUrl = `${SITE_ORIGIN}${canonicalPath}`

    setLink('canonical', null, canonicalUrl)
    setMeta('meta[property="og:url"]', canonicalUrl)

    // ── hreflang ──
    // ja は path のみ、en は `?lang=en` パラメータ + 同 fragment。
    setLink('alternate', 'ja', canonicalUrl)

    const enUrl = canonicalPath === '/' ? `${SITE_ORIGIN}/?lang=en` : `${SITE_ORIGIN}/?lang=en#${r}`

    setLink('alternate', 'en', enUrl)
    setLink('alternate', 'x-default', canonicalUrl)

    // ── robots ──
    // 公開 route は index, follow。ログイン必須 / 非公開 route は noindex, nofollow に
    // 切替えて検索インデックス汚染を防ぐ (ScreenPlaceholder 等の動的 ID も含む)。
    const isPublic = PUBLIC_ROUTES.has(r) || r === '' || r === 'lock'
    const robots = isPublic ? 'index, follow, max-image-preview:large' : 'noindex, nofollow'

    setMeta('meta[name="robots"]', robots)
  }, [route, t, language])
}
